import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
var _excluded = ["onBlur"],
    _excluded2 = ["name", "radios", "collapseError", "disabled", "errorText", "disableErrorContainer", "layout"];
import React, { useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { InputError } from '../Error';
import { COLORS, TYPOGRAPHY } from '../../../../themes';
import { flex, hoverChild, isKeyboard, highContrastBorder2, TRANSITION_DEFAULT, hoverChildMultiple } from '../../../../styling';
import { useExtendedField } from '../../../../hooks';
import { dotToDash, toAlphanumericDashString } from '../../../../utils';
import { Div, Span } from '../../../Styled';
import { IS_DEV } from '../../../../constants';
import { HelperText } from '../../../text';
var ItemContainer = /*#__PURE__*/styled(Div).withConfig({
  displayName: "RadioInput__ItemContainer",
  componentId: "sc-skjjgw-0"
})(["flex:0 0 auto;", " position:relative;", " ", " ", ""], flex('flex-start', 'flex-start'), hoverChildMultiple('.styled-input', [{
  prop: 'background',
  base: 'none',
  hover: COLORS.PRIMARY_200
}, {
  prop: 'border',
  base: "0.2rem solid ".concat(COLORS.PRIMARY_500),
  hover: "0.2rem solid ".concat(COLORS.PRIMARY_500),
  active: "0.2rem solid ".concat(COLORS.PRIMARY_700)
}]), hoverChild('.center', 'background', COLORS.PRIMARY_500, COLORS.PRIMARY_500, COLORS.PRIMARY_700), function (_ref) {
  var $focus = _ref.$focus;
  return $focus && css([".styled-input{box-shadow:0 0 0 2px ", ";border:0.2rem solid ", ";outline:2px solid transparent;}"], COLORS.FOCUS_DARK, COLORS.FOCUS_DARK);
});
var Input = /*#__PURE__*/styled.input.withConfig({
  displayName: "RadioInput__Input",
  componentId: "sc-skjjgw-1"
})(["width:2.4rem;height:2.4rem;margin-right:12px;flex:0 0 auto;"]);
var StyledInput = /*#__PURE__*/styled.div.withConfig({
  displayName: "RadioInput__StyledInput",
  componentId: "sc-skjjgw-2"
})(["position:absolute;top:0;left:0;pointer-events:none;width:2.4rem;height:2.4rem;box-sizing:border-box;border-radius:50%;", ";"], flex());
var Center = /*#__PURE__*/styled.div.withConfig({
  displayName: "RadioInput__Center",
  componentId: "sc-skjjgw-3"
})(["width:1.4rem;height:1.4rem;border-radius:50%;box-sizing:border-box;", ";background:", ";transform:scale(", ");transition:transform ", ",background ", " !important;"], highContrastBorder2, COLORS.PRIMARY_500, function (_ref2) {
  var $active = _ref2.$active;
  return $active ? 1 : 0;
}, TRANSITION_DEFAULT, TRANSITION_DEFAULT);
var Label = /*#__PURE__*/styled.label.withConfig({
  displayName: "RadioInput__Label",
  componentId: "sc-skjjgw-4"
})(["cursor:pointer;> *{&:first-child{padding-top:0.2rem;}}"]);

var RadioItem = function RadioItem(_ref3) {
  var $css = _ref3.$css,
      id = _ref3.id,
      name = _ref3.name,
      onChange = _ref3.onChange,
      value = _ref3.value,
      label = _ref3.label,
      disabled = _ref3.disabled,
      helperText = _ref3.helperText;

  var _useExtendedField = useExtendedField({
    name: name,
    value: value,
    type: 'radio'
  }, {
    onChange: onChange
  }),
      _useExtendedField2 = _slicedToArray(_useExtendedField, 2),
      _useExtendedField2$ = _useExtendedField2[0],
      _onBlur = _useExtendedField2$.onBlur,
      formikInputConfig = _objectWithoutProperties(_useExtendedField2$, _excluded),
      formikValue = _useExtendedField2[1].value;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      focused = _useState2[0],
      setFocused = _useState2[1];

  return /*#__PURE__*/React.createElement(ItemContainer, {
    $css: $css,
    $active: "".concat(value) === "".concat(formikValue),
    $focus: focused,
    $disabled: disabled
  }, /*#__PURE__*/React.createElement(Input, _extends({
    type: "radio",
    id: id,
    "data-test-id": id
  }, formikInputConfig, {
    onFocus: function onFocus(e) {
      setFocused(isKeyboard(e));
    },
    onBlur: function onBlur(e) {
      _onBlur(e);

      setFocused(false);
    },
    disabled: disabled
  })), /*#__PURE__*/React.createElement(StyledInput, {
    className: "styled-input"
  }, /*#__PURE__*/React.createElement(Center, {
    className: "center",
    $active: "".concat(value) === "".concat(formikValue)
  })), /*#__PURE__*/React.createElement(Label, {
    htmlFor: id,
    "data-test-id": "".concat(id, "-label")
  }, /*#__PURE__*/React.createElement(Span, {
    className: "label-string",
    $css: css(["display:block;", " color:", ";"], TYPOGRAPHY.M_SEMIBOLD, COLORS.NEUTRAL_550)
  }, label, helperText && /*#__PURE__*/React.createElement(HelperText, {
    $css: css(["display:block;margin-top:8px;"])
  }, helperText))));
};

var RadioInput = function RadioInput(_ref4) {
  var name = _ref4.name,
      radios = _ref4.radios,
      collapseError = _ref4.collapseError,
      disabled = _ref4.disabled,
      errorText = _ref4.errorText,
      _ref4$disableErrorCon = _ref4.disableErrorContainer,
      disableErrorContainer = _ref4$disableErrorCon === void 0 ? false : _ref4$disableErrorCon,
      _ref4$layout = _ref4.layout,
      layout = _ref4$layout === void 0 ? 'row' : _ref4$layout,
      rest = _objectWithoutProperties(_ref4, _excluded2);

  if (IS_DEV) {
    if (radios.length !== 2) console.error('RadioInput must have 2 values');
  }

  var _useExtendedField3 = useExtendedField({
    name: name
  }),
      _useExtendedField4 = _slicedToArray(_useExtendedField3, 2),
      error = _useExtendedField4[1].errorText;

  var idRef = useRef(dotToDash(name)).current;
  return /*#__PURE__*/React.createElement(Div, rest, /*#__PURE__*/React.createElement(Div, {
    className: "radios-wrapper",
    $css: css(["", ""], layout === 'row' ? css(["", " >{&:not(:last-of-type){margin-right:24px;}}"], flex('flex-start', 'flex-start', 'row', 'wrap')) : css([">{&:not(:last-of-type){margin-bottom:26px;}}"]))
  }, radios.map(function (_ref5, index) {
    var label = _ref5.label,
        value = _ref5.value,
        helperText = _ref5.helperText;
    return /*#__PURE__*/React.createElement(RadioItem, {
      disabled: disabled,
      id: "".concat(idRef, "-").concat(index, "-").concat(value),
      label: label,
      name: name,
      value: value,
      helperText: helperText,
      key: toAlphanumericDashString("".concat(value))
    });
  })), !disableErrorContainer && /*#__PURE__*/React.createElement(InputError, {
    $css: css(["margin-top:6px;"]),
    collapseError: collapseError,
    errorId: idRef,
    errorText: errorText || error
  }));
};

export default RadioInput;